/**
 * Dependencies
 */
import _ from 'lodash';

export default {
    props: ['title', 'products', 'productType', 'activeModelSearchTerm'],
    data() {
        return {
            reverseDirection: false,
            wrap: null,
            scroller: null,
            allCells: [],
            fixedCell: null,
            visibleCells: [],
            windowWidth: window.innerWidth,
        };
    },

    mounted() {
        // Set properties
        this.wrap = this.$el.querySelector('.sticky-table__scroller');
        this.scroller = this.wrap.querySelector('.table');
        this.allCells = this.wrap.querySelectorAll('th');
        this.fixedCell = this.allCells[0];

        // Set eventListener running
        window.addEventListener('resize', _.debounce(() => {
            this.windowWidth = window.innerWidth;
        }, 500));
    },

    methods: {
        /**
         * Click event handler
         */
        handleClick() {
            // If at end of table
            if (this.reverseDirection) {
                this.scroller.style.left = '0px';
                this.scroller.addEventListener('transitionend', this.atStart);
                return false;
            }

            // IntersectionObserver options
            const options = {
                root: this.wrap,
                rootMargin: `0px 0px 0px -${this.fixedCell.getBoundingClientRect().width}px`,
                threshold: 1.0,
            };

            // IntersectionObserver
            const observer = new IntersectionObserver((entries) => {

                entries.forEach((entry) => {
                    // Stop observing entry
                    observer.unobserve(entry.target);
                });

                this.visibleCells = entries.filter((entry) => {

                    // Stop observing entry
                    observer.unobserve(entry.target);

                    // If entry is intersecting at all
                    return entry.intersectionRatio > 0;

                }).filter((entry, index, array) => {

                    // If there are multiple intersecting cells, return fully intersected
                    if (array.length > 1) return entry.intersectionRatio > 0.99;

                    // Return single cell however intersected
                    return entry;

                });
            }, options);

            // Fire IntersectionObserver for each cell
            this.allCells.forEach((cell) => {
                observer.observe(cell);
            });

            // Exlicit return
            return undefined;
        },

        atStart() {
            this.scroller.removeEventListener('transitionend', this.atStart);
            this.reverseDirection = false;
        },

        atEnd() {
            this.scroller.removeEventListener('transitionend', this.atEnd);
            this.reverseDirection = true;
        },

        getVerticalMargin(product) {
            // Buffer space for sticky column on left so that multi-row fields are still alligned
            return this.productType === 'pvModule' ? product.productQualityMeasures.length : product.softwareClients.length;
        }
    },

    watch: {
        visibleCells(cells) {
            // Get existing amount
            const existingAmount = Math.abs(this.scroller.style.left ? parseInt(this.scroller.style.left, 10) : 0);

            // Calculate scroll amount
            const scrollAmount = cells.reduce((total, cell) => {
                if (cell.target.classList.contains('last')) {
                    return total;
                }

                const cellWidth = cell.target.getBoundingClientRect().width;
                return total + cellWidth;
            }, 0);

            // Calculate total
            const totalAmount = existingAmount + scrollAmount;

            // How much is remaining?
            const remainingPx = this.scroller.getBoundingClientRect().width - this.wrap.getBoundingClientRect().width;

            if (remainingPx > totalAmount) {
                this.scroller.style.left = `-${totalAmount}px`;
            } else {
                this.scroller.style.left = `-${remainingPx}px`;
                this.scroller.addEventListener('transitionend', this.atEnd);
            }
        },

        windowWidth: function (newVal, oldVal){ // eslint-disable-line
            // If window was narrowed, stop.
            if (newVal - oldVal <= 0) return false;

            // Get amount table has been scrolled by and process into a positive integer
            const alreadyScrolled = Math.abs(parseInt(this.scroller.style.left, 10));
            // If not a number, stop.
            if (!alreadyScrolled) return false;

            // console.log(alreadyScrolled);

            // Get widths of elements
            const wrapWidth = this.wrap.getBoundingClientRect().width;
            const scrolledWidth = this.scroller.getBoundingClientRect().width;

            // Get difference
            const difference = scrolledWidth - wrapWidth;

            // If difference is less than amount scrolled, stop.
            if (difference > alreadyScrolled) return false;

            // Update scroll left value
            this.scroller.style.left = `-${difference}px`;
            this.scroller.addEventListener('transitionend', this.atEnd);
        },

    },

    template: `
        <div :class="{ 'is-at-end': reverseDirection === true }">
            <h2 class="products-entry__title h4 products-entry__subtitle mb-md" v-if="title">{{title}}</h2>

            <div class="sticky-table mb-lg"">
                <div class="sticky-table__keys">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Model Number</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="product in products">
                                <tr :class="{'highlighted' : activeModelSearchTerm === product.modelNumber}">
                                    <td>
                                        {{product.modelNumber}}
                                        <br
                                            v-for="i in getVerticalMargin(product)"
                                            :key="i"
                                        >
                                    </td>
                                </tr>
                            </template>

                        </tbody>
                    </table>
                </div>

                <template v-if="productType === 'pvModule'">
                    <div class="sticky-table__scroller">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th class="first">Model Number</th>
                                    <th>Enhanced Listings</th>
                                    <th>Approval Date</th>
                                    <th>Expiry Date</th>
                                    <th>Certificate Number</th>
                                    <th class="last">Certifying Body</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="product in products">
                                    <tr :class="{'highlighted' : activeModelSearchTerm === product.modelNumber}">
                                        <td class="first">{{ product.modelNumber }}</td>

                                        <td v-if="product.productQualityMeasures">
                                            <span v-for="(qualityMeasure, i) in product.productQualityMeasures" :key="i">
                                                {{ qualityMeasure }}
                                                <br v-if="i != product.productQualityMeasures.length">
                                            </span>
                                        </td>
                                        <td v-else>-</td>

                                        <td v-if="product.productCecApprovalDate">{{product.productCecApprovalDate}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.cecExpiryDate">{{product.cecExpiryDate}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.certificateNumber">{{product.certificateNumber}}</td>
                                        <td v-else>-</td>

                                        <td class="last" v-if="product.certifyingBody">{{product.certifyingBody}}</td>
                                        <td v-else>-</td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>

                <template v-if="productType === 'inverter'">
                    <div class="sticky-table__scroller">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th class="first">Model Number</th>
                                    <th>Certificate Number</th>
                                    <th>Series Type</th>
                                    <th>Software Client</th>
                                    <th>Approval Date</th>
                                    <th>Expiry Date</th>
                                    <th>Number of Phases</th>
                                    <th>Rated AC Output (VA)</th>
                                    <th>Integrated PV Isolator</th>
                                    <th>Integrated PV Isolator EESS Registered*</th>
                                    <th class="last">Inverter Grid Settings Access Method</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="product in products">
                                    <tr :class="{'highlighted' : activeModelSearchTerm === product.modelNumber}">
                                        <td class="first">{{product.modelNumber}}</td>

                                        <td v-if="product.certificateNumber">{{product.certificateNumber}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.productSeries">{{product.productSeries}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.softwareClients">
                                            <span v-for="(client, i) in product.softwareClients" :key="i">
                                                {{ client }}
                                                <br v-if="i != product.softwareClients.length">
                                            </span>
                                        </td>
                                        <td v-else>-</td>

                                        <td v-if="product.cecApprovedDate">{{product.cecApprovedDate}}</td>
                                        <td v-else>No</td>

                                        <td class="last" v-if="product.cecExpiryDate">{{product.cecExpiryDate}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.numberPhases">{{product.numberPhases}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.ratedVar">{{product.ratedVar}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.integratedPvIsolator">{{product.integratedPvIsolator}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.pcIsolatorEessRegistered">{{product.pcIsolatorEessRegistered}}</td>
                                        <td v-else>-</td>

                                        <td v-if="product.inverterGridSettingsAccessMethod && product.inverterGridSettingsAccessMethod.length" class="last">{{ product.inverterGridSettingsAccessMethod.join(', ') }}</td>
                                        <td v-else class="last">-</td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>

                <div class="sticky-table__trigger">
                    <button class="js-scroll sticky-table__button" @click="handleClick()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                            <path d="M5.5 0l-2 2 6 6-6 6 2 2 8-8-8-8z"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    `,
};
